import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "sourceUrl", "clippingImageField", "clippingThumbnailField", "canvasStateField",
    "clippingPreview", "clippingPreviewImg",
    "modal", "errorBanner", "noticeBanner", "noticeText", "loadingSpinner", "loadingLabel",
    "sendToTeamOffer", "articleLink", "sendToTeamInstall",
    "metadataSection", "metaImageUrl", "metaTitle", "metaDescription", "metaImagePreview",
    "logoSection", "logoPreview", "logoDomain", "logoButtonText",
    "editorSection", "uploadOverlay",
    "directUploadInput", "directUploadStatus", "imageDropZone",
    "imageSourceSection", "customImageUrl", "photoPicker", "photoGrid", "backgroundUpload"
  ]

  static values = {
    proxyUrl: String,
    logoLookupUrl: String,
    logoUploadUrl: String,
    directUploadUrl: String,
    metadataLookupUrl: String,
    readPageUrl: String,
    photosUrl: String,
    intern: Boolean
  }

  connect() {
    this.currentLogoUrl = null
    this.currentDomain = null
    // DEF-4: the clipping editor reports every image failure up here so it
    // lands in the (auto-scrolled) banner instead of the console. It lands
    // in the ERROR slot, beside any notice rather than over it.
    //
    // The Preview is cleared first when the failed image is the article's:
    // the preview loads straight into her browser, which the outlet lets
    // through, while the canvas loads through our server, which it does
    // not. A preview of a picture the canvas cannot use is the thumbnail
    // lying to her.
    this._imageErrorHandler = (event) => {
      this._clearUnusableImage(event.detail.url)
      this.showError(event.detail.message)
    }
    this.element.addEventListener("clipping-editor:imageError", this._imageErrorHandler)
    // ...and a red "could not be loaded" line goes away once an image does
    // load, instead of sitting over a photo that worked. A message named in
    // `clears` goes only while it is still the one showing.
    this._imageLoadedHandler = (event) => {
      const clears = event.detail?.clears
      if (!clears || this.errorBannerTarget.textContent === clears) this.hideError()
    }
    this.element.addEventListener("clipping-editor:imageLoaded", this._imageLoadedHandler)
    // Send to TEAM (Send_To_TEAM_Plan.md): the small window the bookmarks-bar
    // button opens passes the article she is reading to every TEAM tab.
    this._sendToTeam = new BroadcastChannel("send-to-team")
    this._sendToTeam.onmessage = ({ data }) => this._articleSent(data)
  }

  disconnect() {
    this.element.removeEventListener("clipping-editor:imageError", this._imageErrorHandler)
    this.element.removeEventListener("clipping-editor:imageLoaded", this._imageLoadedHandler)
    this._sendToTeam.close()
  }

  // Enter never saves the clip: it used to submit the form, so a pasted
  // link (or a typed outlet name) plus Enter saved a clip with no image
  // (2026-09-24). In Source URL it means Generate; elsewhere it does
  // nothing, and saving is the button's job.
  enterKey(event) {
    const el = event.target
    if (el.closest("textarea, button, a, [type=submit]")) return   // Save, Cancel and the rest keep Enter
    event.preventDefault()
    if (el === this.sourceUrlTarget) this.openModal()
  }

  openModal() {
    const url = this.sourceUrlTarget.value.trim()
    if (!url) {
      this.showError("Please enter a source URL first.")
      return
    }

    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Reset state
    this.metadataSectionTarget.classList.add("hidden")
    this.logoSectionTarget.classList.add("hidden")
    this.editorSectionTarget.classList.add("hidden")
    if (this.hasImageSourceSectionTarget) this.imageSourceSectionTarget.classList.add("hidden")
    this.loadingSpinnerTarget.classList.remove("hidden")
    if (this.hasLoadingLabelTarget) {
      this.loadingLabelTarget.textContent = `Reading ${this._hostOf(url)}...`
    }
    this.hideError()
    this.hideNotice()

    // A different link is a different article: start its fields empty.
    // The SAME link again (closed with the X, then Generate) keeps what she
    // typed, because fetchMetadata only fills a field that is empty.
    if (url !== this._lastLookupUrl) {
      this._sentImage = null
      this.metaImageUrlTarget.value = ""
      this.metaTitleTarget.value = ""
      this.metaDescriptionTarget.value = ""
      this.updateImagePreview()
    }
    this._lastLookupUrl = url
    this._editingSaved = false
    this._lookupFailed = false
    this._articleWaiting = false

    // A lookup she has walked away from (closed, then Generated again) must
    // not fill the next modal's fields, show the wrong outlet's notice, or
    // rebuild a canvas she is already working on. The refused path can
    // take seconds, which is plenty of time to click twice.
    const lookup = this._lookup = (this._lookup || 0) + 1
    const current = () => lookup === this._lookup

    // Fetch metadata and logo in parallel. Send to TEAM waits on this
    // before it fills anything, so a lookup still running cannot undo it.
    this._lookupDone = Promise.all([
      this.fetchMetadata(url, current),
      this.fetchLogo(url, current)
    ]).finally(() => {
      if (!current()) return
      this.loadingSpinnerTarget.classList.add("hidden")
      this.metadataSectionTarget.classList.remove("hidden")
      this.logoSectionTarget.classList.remove("hidden")
      this.editorSectionTarget.classList.remove("hidden")
      if (this.hasImageSourceSectionTarget) this.imageSourceSectionTarget.classList.remove("hidden")

      // Initialize the Fabric.js editor
      const editorController = this.application.getControllerForElementAndIdentifier(
        this.editorSectionTarget, "clipping-editor"
      )
      let built
      if (editorController) {
        // Raw URL: the clipping editor's _loadImage choke point normalizes
        // and proxies every canvas-bound URL in one place (DEF-3).
        const imageUrl = this._forCanvas(this.metaImageUrlTarget.value.trim()) || null
        built = editorController.initEditor(
          imageUrl,
          this.metaTitleTarget.value.trim(),
          this.metaDescriptionTarget.value.trim(),
          this.currentLogoUrl
        )
        // Staggered recalculations to catch modal layout settling
        const recalc = () => { if (editorController) editorController.recalcOffset() }
        requestAnimationFrame(() => {
          recalc()
          requestAnimationFrame(() => {
            recalc()
            setTimeout(recalc, 200)
            setTimeout(recalc, 500)
          })
        })
      }

      // Put the cursor where her next action is. When the lookup came back
      // with nothing, the title is the first thing she has to produce and
      // she is going to produce it by hand; landing her in the field saves
      // a click she makes eight to ten times a sitting.
      // preventScroll: the notice above it is the explanation, and it must
      // stay on screen.
      if (!this.metaTitleTarget.value.trim()) this.metaTitleTarget.focus({ preventScroll: true })
      // Settled means the canvas is built, picture loaded: Send to TEAM
      // asks "is there a background yet?" and must not ask too early
      // (seat C reconvene).
      return built
    })
  }

  // The host as the address bar shows it. Used only for a label, so a URL
  // too broken to parse just yields the trimmed text.
  _hostOf(url) {
    try {
      return new URL(this._articleHref(url))
        .hostname.replace(/^www\./i, "")
    } catch {
      return url
    }
  }

  closeModal() {
    // Walking away cancels any lookup still running, so it cannot fill the
    // fields or build a canvas behind a closed editor.
    this._lookup = (this._lookup || 0) + 1
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""

    // Dispose the canvas, and retire a build still waiting on the drawing
    // library or a background, for the same reason as the lookup above.
    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (editorController) {
      editorController.close()
    }
  }

  editExistingClipping(event) {
    const button = event.currentTarget
    const clippingUrl = button.dataset.clippingUrl
    const clippingState = button.dataset.clippingState || ''

    if (!clippingUrl) {
      this.showError("No clipping image found to edit.")
      return
    }

    // Open the modal
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._editingSaved = true

    // Hide metadata, logo, and loading sections (not needed when editing
    // existing). The image-source picker STAYS visible (DEF-5, register
    // 2026-08-04): staff editing an existing clip can pick a different
    // background; DEF-4's load-before-destroy plus the DEF-6 init token
    // make a failed or superseded pick harmless.
    this.metadataSectionTarget.classList.add("hidden")
    this.logoSectionTarget.classList.add("hidden")
    this.loadingSpinnerTarget.classList.add("hidden")
    if (this.hasImageSourceSectionTarget) this.imageSourceSectionTarget.classList.remove("hidden")
    this.hideError()
    this.hideNotice()

    // Show the editor section
    this.editorSectionTarget.classList.remove("hidden")

    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (editorController) {
      // If we have saved canvas state, restore fully editable clip. Otherwise fall back to
      // loading the rasterized image as a bg (legacy behavior for older clips).
      if (clippingState && clippingState.length > 20) {
        editorController.initEditorFromState(clippingState)
      } else {
        editorController.initEditorFromExisting(clippingUrl)
      }
      const recalc = () => { if (editorController) editorController.recalcOffset() }
      requestAnimationFrame(() => {
        recalc()
        requestAnimationFrame(() => {
          recalc()
          setTimeout(recalc, 200)
          setTimeout(recalc, 500)
        })
      })
    }
  }

  // Metadata comes from OUR server, not from the browser calling a scraper
  // (2026-09-03). The scraper's datacenter IPs are bot-blocked on
  // Blogger-hosted outlets, which handed the canvas a URL for a headline and
  // no background image: the "clips aren't generating" report. The server
  // fetches the page itself and falls back to the vendor; its API key no
  // longer rides to every browser in a data attribute.
  async fetchMetadata(url, current = () => true) {
    try {
      const response = await fetch(
        `${this.metadataLookupUrlValue}?url=${encodeURIComponent(url)}`,
        { headers: { "Accept": "application/json" } }
      )
      const data = await response.json()
      if (!current()) return
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)

      this._lookupFailed = data.outcome !== "ok"
      if (data.outcome === "ok") this._fillFromAnswer(data)
      // An article she sent is about to fill this; the refusal sentence
      // would tell her to click the button she just clicked.
      if (!this._articleWaiting) this._noticeFor(data, url)
    } catch (error) {
      if (!current()) return
      console.error("Metadata fetch failed:", error)
      // The only state where "again" is honest: this is OUR server or the
      // network, not the outlet, and it usually works on the next try.
      this.showError("TEAM could not reach its own server just now. Close this and click Generate Clipping again. If it happens twice, fill the fields in below.")
    }
  }

  // Fill only from a real article, and only a field that is empty.
  // "unreadable" carries the page's leftovers ("Spotify - Web Player: Music
  // for everyone", a site's own name), which would land right under the
  // sentence telling her to type the details herself (senior committee
  // 2026-09-23). And a headline she corrected is never overwritten by a
  // second Generate on the same link. These are the editor's own Article
  // Title and Description, never the form's Title, which holds the OUTLET
  // name. One method for both readers: our server's lookup and Send to
  // TEAM's. Answers the names of the fields it filled.
  _fillFromAnswer(data) {
    const filled = []
    if (data.image && !this.metaImageUrlTarget.value.trim()) {
      this.metaImageUrlTarget.value = data.image
      this._showImagePreview(data.image)
      filled.push("image")
    }
    if (data.title && !this.metaTitleTarget.value.trim()) {
      this.metaTitleTarget.value = data.title
      filled.push("title")
    }
    if (data.description && !this.metaDescriptionTarget.value.trim()) {
      this.metaDescriptionTarget.value = data.description
      filled.push("description")
    }
    return filled
  }

  // Trust the server's own verdict rather than re-deriving it from "are all
  // three fields blank". A dead Spotify link or a removed SoundCloud track
  // answers with the site's name and tagline: not blank, and not an answer
  // either.
  //
  // Each failure has its own first action, so its own sentence (register
  // 2026-09-22; the fourth 2026-09-23). REFUSED: the link is fine, checking
  // it is a wasted trip. UNANSWERED: the site may be down or the link
  // mistyped, so check the link first. UNREADABLE: the page opened, so
  // check it is the article itself, because a homepage or a dead player
  // page looks like this. The first two offer Send to TEAM (2026-10-01):
  // her browser opens what our server could not. Every sentence here was
  // approved verbatim by the owner ("Wording approved", 2026-10-01).
  _noticeFor(data, url) {
    const host = data.host || this._hostOf(url)
    const orType = "or type the title and description below and pick a DPK photo or upload an image as the background."
    if (data.outcome === "refused") {
      this.showNotice(
        `${host} blocks our server. The link is fine, but TEAM cannot read this page and trying again will not help. ` +
        `On the article's page, click Send to TEAM in your bookmarks bar, ${orType}`,
        { offer: true }
      )
    } else if (data.outcome === "unanswered") {
      this.showNotice(
        `${host} did not answer. The site may be down or the link may be mistyped, so check the link first. ` +
        `If it opens in your browser, click Send to TEAM in your bookmarks bar on the article's page, ${orType}`,
        { offer: true }
      )
    } else if (data.outcome === "unreadable") {
      this.showNotice(
        "This page opened but has no article details on it. A homepage, a player page or a dead link looks like this, so check the link goes to the article itself. " +
        "If it does, type the title and description below, then pick a DPK photo or upload an image as the background."
      )
    }
  }

  async fetchLogo(url, current = () => true) {
    this.logoClipOnly = this.internValue   // decided before the lookup, so a failed lookup cannot undo it
    this.currentDomain = this._hostOf(url)
    this.logoDomainTarget.textContent = this.currentDomain
    // The same link again keeps the logo she added to this clip (it lives
    // nowhere else). A different link starts with none, never the last
    // clip's logo, even if the lookup below fails.
    const kept = this._clipLogo && this._clipLogo.link === url ? this._clipLogo.url : null
    this._showLogo(kept)
    try {
      const response = await fetch(`${this.logoLookupUrlValue}?domain=${encodeURIComponent(this.currentDomain)}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      if (!current()) return

      // A logo that belongs to THIS clip only, never saved to an outlet:
      // on a platform link (one Spotify "outlet" would hand one artist's
      // cover to every Spotify clip for every client, 2026-09-23), and for
      // interns, who may not change an outlet's shared logo. Everyone else
      // is told that an upload here is shared, because not knowing that is
      // how the cover got onto Spotify.
      this.logoClipOnly = this.logoClipOnly || !!(data && data.clip_only)
      if (!kept) this._showLogo(data && data.url)
      this.logoDomainTarget.textContent = this.logoClipOnly
        ? `${this.currentDomain} (a logo added here is for this clip only)`
        : `${this.currentDomain} (a logo added here is saved as this outlet's logo for every client)`
    } catch (error) {
      console.error("Logo lookup failed:", error)
    }
  }

  updateImagePreview() {
    const url = this.metaImageUrlTarget.value
    if (url) {
      this._showImagePreview(url)
    } else {
      this.metaImagePreviewTarget.replaceChildren()
      const span = document.createElement("span")
      span.className = "text-xs text-[var(--color-mc-text-secondary)]"
      span.textContent = "No image"
      this.metaImagePreviewTarget.appendChild(span)
    }
  }

  // Forget the article image when it is the one the canvas could not load.
  // Only that one: a failed DPK photo says nothing about the article image.
  _clearUnusableImage(failedUrl) {
    const current = this.metaImageUrlTarget.value.trim()
    if (!failedUrl || !current || failedUrl !== this._forCanvas(current)) return

    this.metaImageUrlTarget.value = ""
    this.updateImagePreview()
  }

  // The preview URL is attacker-controlled text: og:image comes verbatim off
  // the outlet's page. Interpolated into innerHTML, an og:image of
  //   x" onerror="fetch(...)
  // closed the src attribute and ran script on OUR origin in the staffer's
  // authenticated session, with the CSRF token in reach (verified
  // 2026-09-03). Build the element instead, so the value can only ever be
  // an attribute VALUE and never markup. The server rejects non-http(s)
  // values too; this is the layer that does not depend on that one.
  _showImagePreview(url) {
    const img = document.createElement("img")
    img.src = this._forCanvas(url)
    img.className = "w-full h-full object-cover"
    this.metaImagePreviewTarget.replaceChildren(img)
  }

  // One place that shows the logo (or its absence) and labels the button.
  _showLogo(url) {
    this.currentLogoUrl = url || null
    if (url) {
      const img = document.createElement("img")
      img.src = url
      img.className = "max-w-full max-h-full object-contain"
      this.logoPreviewTarget.replaceChildren(img)
    } else {
      const span = document.createElement("span")
      span.className = "text-xs text-[var(--color-mc-text-secondary)]"
      span.textContent = "No logo found"
      this.logoPreviewTarget.replaceChildren(span)
    }
    this.logoButtonTextTarget.textContent = url ? "Change Logo" : "Upload Logo"
  }

  // A logo she chose: shown here AND handed to the canvas, which otherwise
  // learns a logo only when it is rebuilt (Reset, a new background).
  _useLogo(url) {
    this._showLogo(url)
    const editor = this.application.getControllerForElementAndIdentifier(this.editorSectionTarget, "clipping-editor")
    if (editor) editor.setLogo(url)
  }

  _dataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  async uploadLogo(event) {
    const file = event.target.files[0]
    if (!file) return

    if (this.logoClipOnly) {
      // Kept in the browser and put on this clip only; the server never sees
      // it. Shrunk first, as a PNG so transparency survives: it rides inside
      // the saved design (canvas_state), and the clip draws it 200px wide at most.
      event.target.value = ""
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        this.notifyLogoProblem("This file type is not supported. Please upload PNG, JPG, or WebP instead.")
        return
      }
      const link = this._lastLookupUrl
      try {
        const url = await this._dataUrl(await this.generateThumbnail(file, 400, "image/png"))
        this._clipLogo = { link, url }
        this._useLogo(url)
      } catch (error) {
        this.notifyLogoProblem("That image could not be read. Please upload a PNG, JPG, or WebP file.")
      }
      return
    }

    const formData = new FormData()
    formData.append("domain", this.currentDomain)
    formData.append("logo", file)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.logoUploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: formData
      })

      const data = await response.json()
      if (!response.ok || (data && data.error)) {
        // A refused file type is the operator's mistake to fix, not a crash:
        // say which formats work, and leave the existing logo alone.
        this.notifyLogoProblem((data && data.error) || "Failed to upload logo.")
      } else if (data && data.url) {
        this._useLogo(data.url)
      }
    } catch (error) {
      this.notifyLogoProblem("Failed to upload logo: " + error.message)
    }

    event.target.value = ""
  }

  async saveClipping() {
    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (!editorController) return
    // No editor yet (the drawing library is still downloading, or failed
    // to): nothing to export. Return before the fields and the upload flag
    // are touched, so the clip itself still saves.
    if (!editorController.hasCanvas) {
      editorController.sayNotReady()
      return
    }

    this.uploadOverlayTarget.classList.remove("hidden")
    // DEF-8: never leave a half-written pair behind. Clear both fields, stage
    // both signed ids, and commit them together only when BOTH uploads land,
    // so a failed save can never be submitted as a half-imaged clip.
    this.clippingImageFieldTarget.value = ""
    this.clippingThumbnailFieldTarget.value = ""
    this._saving = true

    this.element.dataset.clipUploadFailed = "false"

    try {
      const blob = await editorController.exportBlob()

      // Upload full-size clipping image
      const file = new File([blob], "press-clip.jpg", { type: "image/jpeg" })
      const signedId = await this.directUpload(file)

      // Generate and upload thumbnail
      const thumbBlob = await this.generateThumbnail(blob, 400)
      const thumbFile = new File([thumbBlob], "press-clip-thumb.jpg", { type: "image/jpeg" })
      const thumbSignedId = await this.directUpload(thumbFile)

      this.clippingImageFieldTarget.value = signedId
      this.clippingThumbnailFieldTarget.value = thumbSignedId

      // Store editable canvas state so the clip can be truly re-edited later
      if (this.hasCanvasStateFieldTarget) {
        try {
          this.canvasStateFieldTarget.value = editorController.exportCanvasState()
        } catch (e) {
          console.warn("Failed to export canvas state:", e)
        }
      }

      // Show preview
      const previewUrl = URL.createObjectURL(blob)
      this.clippingPreviewImgTarget.src = previewUrl
      this.clippingPreviewTarget.classList.remove("hidden")

      // Saving never touches the clip's own Title: it holds the OUTLET name,
      // not the headline (see the note on that field in the form).

      this.uploadOverlayTarget.classList.add("hidden")
      this.closeModal()
    } catch (error) {
      this.uploadOverlayTarget.classList.add("hidden")
      this.clippingImageFieldTarget.value = ""
      this.clippingThumbnailFieldTarget.value = ""
      this.element.dataset.clipUploadFailed = "true"
      this.showError("Saving the clipping failed: " + error.message + ". Nothing was saved; try again.")
      console.error("Upload failed:", error)
    } finally {
      this._saving = false
    }
  }

  // DEF-8: the outer form must not silently save a clip whose image FAILED
  // to upload (that is how a failed save became an imageless record and read
  // as "nothing happened"). Deliberately narrow: clips with no image at all
  // are legitimate (radio entries), so only an in-flight or failed attempt
  // blocks the submit.
  validateSubmit(event) {
    if (this._saving) {
      event.preventDefault()
      this.showError("The clipping is still uploading. Give it a moment, then save again.")
      return
    }
    if (this.element.dataset.clipUploadFailed === "true") {
      event.preventDefault()
      this.showError("The clipping image did not upload, so this clip would save without it. Try Generate Clipping again, or upload a screenshot.")
    }
  }

  // Live-push the field being typed in, so the FIRST "Lock Background & Add
  // Text" renders what is currently typed, not the snapshot taken when the
  // editor opened (ticket #595). Only THAT field: pushing both on every
  // keystroke reset whatever she had typed straight onto the other canvas
  // box (reconvene 2026-09-22).
  syncMeta(event) {
    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (!editorController) return
    const name = event.target === this.metaDescriptionTarget ? "description" : "title"
    editorController.setText(name, event.target.value.trim())
  }

  resetEditor() {
    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (editorController) {
      editorController.initEditor(
        this._forCanvas(this.metaImageUrlTarget.value.trim()) || null,
        this.metaTitleTarget.value.trim(),
        this.metaDescriptionTarget.value.trim(),
        this.currentLogoUrl
      )
    }
  }

  directUpload(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue)
      upload.create((error, blobData) => {
        if (error) {
          reject(error)
        } else {
          resolve(blobData.signed_id)
        }
      })
    })
  }

  generateThumbnail(blob, maxWidth, type = "image/jpeg") {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(src)
        const scale = Math.min(maxWidth / img.width, 1)
        const canvas = document.createElement("canvas")
        canvas.width = img.width * scale
        canvas.height = img.height * scale
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob((thumbBlob) => {
          if (thumbBlob) {
            resolve(thumbBlob)
          } else {
            reject(new Error("Failed to generate thumbnail"))
          }
        }, type, 0.91)
      }
      img.onerror = () => { URL.revokeObjectURL(src); reject(new Error("Failed to load image for thumbnail")) }
      const src = URL.createObjectURL(blob)
      img.src = src
    })
  }

  handleDirectUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    this._processFileUpload(file)
  }

  // --- Drag-and-drop for clipping image upload ---

  imageDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageDropZoneTarget) {
      this.imageDropZoneTarget.classList.add("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }
  }

  imageDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageDropZoneTarget) {
      this.imageDropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }
  }

  imageDropFile(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasImageDropZoneTarget) {
      this.imageDropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }

    const file = event.dataTransfer.files[0]
    if (!file) return

    const allowed = ["image/jpeg", "image/png", "image/webp"]
    if (!allowed.includes(file.type)) {
      if (this.hasDirectUploadStatusTarget) {
        this.directUploadStatusTarget.textContent = "Only JPG, PNG, and WEBP files are accepted."
        this.directUploadStatusTarget.className = "text-xs text-[#E57373]"
      }
      return
    }

    this._processFileUpload(file)
  }

  // DEF-8: upload the screenshot AND a real thumbnail as TWO blobs. Putting
  // one signed id in both fields ran the conversion job twice against the
  // same blob and left image/thumbnail sharing one record.
  async _processFileUpload(file) {
    const status = (text, cls) => {
      if (!this.hasDirectUploadStatusTarget) return
      this.directUploadStatusTarget.textContent = text
      this.directUploadStatusTarget.className = cls
    }
    status("Uploading...", "text-xs text-[var(--color-mc-text-secondary)]")

    this._saving = true


    this.element.dataset.clipUploadFailed = "false"
    try {
      const signedId = await this.directUpload(file)
      const thumbBlob = await this.generateThumbnail(file, 400)
      const thumbFile = new File([thumbBlob], "press-clip-thumb.jpg", { type: "image/jpeg" })
      const thumbSignedId = await this.directUpload(thumbFile)

      if (this.hasClippingImageFieldTarget) this.clippingImageFieldTarget.value = signedId
      if (this.hasClippingThumbnailFieldTarget) this.clippingThumbnailFieldTarget.value = thumbSignedId

      if (this.hasClippingPreviewTarget && this.hasClippingPreviewImgTarget) {
        this.clippingPreviewImgTarget.src = URL.createObjectURL(file)
        this.clippingPreviewTarget.classList.remove("hidden")
      }
      status("Uploaded!", "text-xs text-[var(--color-status-confirmed)]")
    } catch (error) {
      console.error("Direct upload failed:", error)
      if (this.hasClippingImageFieldTarget) this.clippingImageFieldTarget.value = ""
      if (this.hasClippingThumbnailFieldTarget) this.clippingThumbnailFieldTarget.value = ""
      status("Upload failed. Try again.", "text-xs text-[#E57373]")
      this.element.dataset.clipUploadFailed = "true"
      this.showError("That upload did not go through, so nothing was saved. Try again.")
    } finally {
      this._saving = false
    }
  }

  async showPhotoPicker() {
    if (!this.hasPhotoGridTarget || !this.photosUrlValue) return

    this.photoGridTarget.innerHTML = `<p class="text-xs text-[var(--color-mc-text-secondary)] col-span-4">Loading photos...</p>`
    if (this.hasPhotoPickerTarget) this.photoPickerTarget.classList.remove("hidden")

    try {
      const response = await fetch(this.photosUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (data.photos && data.photos.length > 0) {
        this.photoGridTarget.innerHTML = data.photos.map(photo =>
          `<button type="button" class="aspect-[4/3] rounded overflow-hidden border border-[var(--color-mc-border)] hover:border-[var(--color-mc-accent-terracotta)] transition-colors cursor-pointer"
                  data-action="click->press-clip-editor#selectPhoto"
                  data-photo-url="${photo.full_url}">
            <img src="${photo.thumb_url}" class="w-full h-full object-cover" />
          </button>`
        ).join("")
      } else {
        this.photoGridTarget.innerHTML = `<p class="text-xs text-[var(--color-mc-text-secondary)] col-span-4">No DPK photos found for this client.</p>`
      }
    } catch (error) {
      console.error("Failed to load photos:", error)
      this.photoGridTarget.innerHTML = `<p class="text-xs text-[#E57373] col-span-4">Failed to load photos.</p>`
    }
  }

  // DEF-4: hide the picker only on SUCCESS. A blocked photo used to close
  // the picker and wipe the canvas, which read as "it won't let me pick".
  async selectPhoto(event) {
    const button = event.currentTarget
    const photoUrl = button.dataset.photoUrl
    if (!photoUrl) return

    const ok = await this._reinitEditorWithImage(photoUrl)
    if (ok && this.hasPhotoPickerTarget) this.photoPickerTarget.classList.add("hidden")
  }

  // The outlet's own photo, saved from her browser. Her browser can load
  // what our server is refused, so this is the way the real article image
  // still reaches a clip from an outlet that blocks us (2026-09-22). A file
  // read in the browser never touches our proxy.
  chooseBackgroundFile() {
    if (this.hasBackgroundUploadTarget) this.backgroundUploadTarget.click()
  }

  uploadBackground(event) {
    const file = event.target.files[0]
    event.target.value = ""
    if (!file || !file.type.startsWith("image/")) return

    const reader = new FileReader()
    reader.onload = (e) => this._reinitEditorWithImage(e.target.result)
    reader.readAsDataURL(file)
  }

  loadCustomImage() {
    if (!this.hasCustomImageUrlTarget) return
    const url = this.customImageUrlTarget.value.trim()
    if (!url) return

    // Raw URL: normalization is the clipping editor's job (DEF-3).
    this._reinitEditorWithImage(url)
  }

  async _reinitEditorWithImage(imageUrl) {
    const editorController = this.application.getControllerForElementAndIdentifier(
      this.editorSectionTarget, "clipping-editor"
    )
    if (!editorController) return false

    const ok = await editorController.initEditor(
      this._forCanvas(imageUrl),
      this.metaTitleTarget.value.trim(),
      this.metaDescriptionTarget.value.trim(),
      this.currentLogoUrl
    )

    const recalc = () => { if (editorController) editorController.recalcOffset() }
    requestAnimationFrame(() => {
      recalc()
      requestAnimationFrame(() => {
        recalc()
        setTimeout(recalc, 200)
        setTimeout(recalc, 500)
      })
    })
    return ok !== false
  }

  // ═══ SEND TO TEAM (owner directive 2026-10-01, Send_To_TEAM_Plan.md) ═══
  //
  // Vents and Cowgirl turn away every visitor that is not a real browser,
  // our server included. The bookmarks-bar button reads the article in HER
  // browser and its small window passes it to every TEAM tab. This tab
  // takes it only when it is working on that very article, says so at once
  // (the window gives up waiting after a moment, and a running lookup can
  // take 18 seconds), and fills itself afterwards. Only the article's own
  // host is listened to (the window stamps the origin the browser
  // vouched for), so a page can only ever fill a clip about itself.
  _articleSent(data) {
    if (!data || data.type !== "article" || !data.article) return
    const answer = (kind, extra = {}) => this._sendToTeam.postMessage({ type: "answer", id: data.id, kind, ...extra })
    const open = !this.modalTarget.classList.contains("hidden")
    // Edit Clipping on THIS article's saved clip: closing it and clicking
    // again regenerates that clip in place. Anything else on a saved clip
    // stays silent; "add a new clip" here would make a duplicate.
    if (open && this._editingSaved) {
      return this._samePage(this.sourceUrlTarget.value.trim(), data.article) ? answer("editing-saved") : undefined
    }

    const link = open ? this._lastLookupUrl : this.sourceUrlTarget.value.trim()
    if (!link) return
    if (!this._samePage(link, data.article)) return open ? answer("other-link", { link }) : undefined

    answer("taken")
    // Her link is in Source URL but the editor is closed: Generate, as her
    // click would, then fill it the same way.
    if (!open) {
      this.openModal()
      this._articleWaiting = true
      if (this.hasLoadingLabelTarget) this.loadingLabelTarget.textContent = "Filling in the article you sent..."
    }
    this._fillFromArticle(data.article)
  }

  // The same article: the browser's origin is the link's host, and the page
  // it was on (or the page's own canonical address) is the link, ignoring
  // the scheme, www, any query or fragment (email trackers), and a
  // trailing slash.
  _samePage(link, article) {
    const key = (value) => {
      try {
        const url = new URL(this._articleHref(value))
        return `${url.hostname.replace(/^www\./i, "").toLowerCase()}${url.pathname.replace(/\/+$/, "")}`
      } catch (error) {
        return null
      }
    }
    const want = key(link)
    const host = (value) => (key(value) || "").split("/")[0]
    if (!want || host(article.origin) !== want.split("/")[0]) return false
    return [article.url, article.canonical].some((value) => value && key(value) === want)
  }

  // The article's tags are read on OUR server by the lookup's own parser
  // (one reader decides what an article is, and a click while the outlet's
  // "Just a moment..." check is still up is caught there), then the answer
  // fills the editor through the lookup's own method.
  async _fillFromArticle(article) {
    const lookup = this._lookup
    await this._lookupDone?.catch(() => {})
    if (lookup !== this._lookup) return   // closed, or moved on to another link
    this._articleWaiting = false

    let data
    try {
      const response = await fetch(this.readPageUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ url: this._lastLookupUrl, page_url: article.url, page_head: article.head })
      })
      data = await response.json()
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
    } catch (error) {
      if (lookup !== this._lookup) return
      console.error("Send to TEAM read failed:", error)
      this.showError("TEAM could not reach its own server just now. Click Send to TEAM on the article again.")
      return
    }
    if (lookup !== this._lookup) return

    this.hideError()
    if (data.source === "challenge") {
      return this.showNotice("The article had not finished loading when you clicked Send to TEAM. Wait until you can read it, then click Send to TEAM again.")
    }
    if (data.outcome !== "ok") return this._noticeFor(data, this._lastLookupUrl)

    // The picture: the bytes her browser read, kept here so Reset and the
    // preview use them, while Image URL shows the article's own address.
    // With no bytes, the address alone goes nowhere on a site our server
    // just failed to read (our server would fetch it), so it is left out
    // and she is told how to bring the picture herself.
    // The bytes are named by the address the button read them from, never
    // by our parse of it: the two can differ in a letter's case or an
    // accent, and the button installed today must keep working whatever our
    // parser does later (seat A reconvene).
    const bytes = article.image || null
    if (bytes) this._sentImage = { url: article.imageUrl, data: bytes }
    const imageUrl = bytes ? article.imageUrl : (this._lookupFailed ? null : data.image)
    const filled = this._fillFromAnswer({ ...data, image: imageUrl })

    const editor = this.application.getControllerForElementAndIdentifier(this.editorSectionTarget, "clipping-editor")
    const background = editor && editor.hasBackground
    let framed = false
    if (editor && !background && filled.includes("image")) {
      framed = await this._reinitEditorWithImage(this.metaImageUrlTarget.value.trim())
      if (lookup !== this._lookup) return
    }
    // A rebuilt canvas took the words from the fields. Otherwise (her own
    // background kept, or the picture would not load) they go onto it here.
    if (editor && !framed) {
      for (const name of filled.filter((field) => field !== "image")) {
        editor.fillText(name, name === "title" ? this.metaTitleTarget.value.trim() : this.metaDescriptionTarget.value.trim())
      }
    }

    if (framed) {
      this.showNotice("Filled in from the article with Send to TEAM. Frame the picture, then click Lock Background & Add Text.")
    } else if (!background) {
      this.showNotice("The title and description came through, but not the picture. On the article, right-click the picture, choose Copy Image, then come back here and press Cmd+V.")
    } else if (filled.length) {
      this.showNotice("Filled in from the article with Send to TEAM. Your background was kept.")
    } else {
      this.showNotice("TEAM already had this article's details, so nothing changed.")
    }
  }

  // The image the canvas and preview should use for an Image URL: the
  // bytes Send to TEAM brought for exactly that address, else the address.
  _forCanvas(url) {
    return this._sentImage && url === this._sentImage.url ? this._sentImage.data : url
  }

  // The error banner lives INSIDE the modal, so it is invisible when the
  // modal is closed (the submit-guard case). Surface where the user is
  // actually looking (DEF-4: no message may render off-screen or hidden).
  // The logo upload can happen with the modal open or closed, so it uses the
  // app-wide toast rather than the modal's error banner. showError is the
  // fallback for the case where the toast controller is not mounted.
  notifyLogoProblem(message) {
    if (window.showToast) {
      window.showToast("error", message)
    } else {
      this.showError(message)
    }
  }

  showError(message) {
    const modalOpen = this.hasModalTarget && !this.modalTarget.classList.contains("hidden")
    if (!modalOpen && this.hasDirectUploadStatusTarget) {
      this.directUploadStatusTarget.textContent = message
      this.directUploadStatusTarget.className = "text-xs text-[#E57373]"
      this.directUploadStatusTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
      return
    }
    this.errorBannerTarget.textContent = message
    this.errorBannerTarget.classList.remove("hidden")
    // DEF-4: the banner sits at the top of a tall scrolling modal while the
    // buttons are at the bottom, so an un-scrolled error reads as "nothing
    // happened" (the original save-does-nothing report).
    this.errorBannerTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  hideError() {
    this.errorBannerTarget.classList.add("hidden")
    this.errorBannerTarget.textContent = ""
  }

  // The situation, not an event. It stays up for the life of the modal so
  // a later image failure cannot erase the only explanation she has, and
  // it scrolls into view for the same reason the error does (DEF-4: the
  // banner is at the top of a tall modal whose buttons are at the bottom).
  //
  // `offer` adds Send to TEAM under the sentence: a link to the article
  // (it usually arrived by email, so no tab is open on it) and the button
  // to drag onto the bookmarks bar, for the first time.
  showNotice(message, { offer = false } = {}) {
    if (!this.hasNoticeBannerTarget) return this.showError(message)

    this.noticeTextTarget.textContent = message
    this.sendToTeamOfferTarget.classList.toggle("hidden", !offer)
    this.sendToTeamInstallTarget.classList.add("hidden")
    if (offer) this.articleLinkTarget.href = this._articleHref(this._lastLookupUrl)
    this.noticeBannerTarget.classList.remove("hidden")
    this.noticeBannerTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  hideNotice() {
    if (!this.hasNoticeBannerTarget) return
    this.noticeBannerTarget.classList.add("hidden")
    this.noticeTextTarget.textContent = ""
    this.sendToTeamOfferTarget.classList.add("hidden")
  }

  showSendToTeamButton() {
    this.sendToTeamInstallTarget.classList.remove("hidden")
  }

  // The link as a browser opens it (the Source URL may have no scheme).
  _articleHref(url) {
    return /^https?:\/\//i.test(url || "") ? url : `https://${url || ""}`
  }
}
